// What the page and the solver's worker say to each other. A message from
// another thread arrives as `any`, so each side reads it here as unknown.

import * as z from "zod/mini"
import type { TableState } from "../domain/engine.ts"
import type { Way } from "../domain/way.ts"
import { moveJsonOf, MoveJson, moveOf } from "./rites.ts"

const RING = z.union([z.literal(-1), z.literal(0), z.literal(1), z.literal("folded")])
const RINGS = z.union([z.tuple([RING]), z.tuple([RING, RING]), z.tuple([RING, RING, RING])])
const CARD = z.object({ id: z.int(), rings: RINGS })
const WIDTH = z.union([z.literal(1), z.literal(2), z.literal(3)])
const TABLE = z.object({
  width: WIDTH,
  cols: z.array(z.object({ mirror: z.boolean(), tide: z.boolean(), cards: z.array(CARD) })),
  altars: z.array(z.union([CARD, z.literal("empty")])),
  nextId: z.int(),
})
const AT_LEAST_ONE = z.int().check(z.minimum(1))

export type Question =
  | { readonly id: number; readonly ask: "path"; readonly state: TableState; readonly maxNodes: number }
  | { readonly id: number; readonly ask: "way"; readonly states: readonly TableState[]; readonly maxNodes: number }

const QUESTION = z.union([
  z.object({ id: z.number(), ask: z.literal("path"), state: TABLE, maxNodes: AT_LEAST_ONE }),
  z.object({ id: z.number(), ask: z.literal("way"), states: z.array(TABLE), maxNodes: AT_LEAST_ONE }),
])

export const parseQuestion = (data: unknown): Question | undefined => {
  const r = QUESTION.safeParse(data)
  return r.success ? r.data : undefined
}

export type PathVerdict = "path" | "none" | "unknown"

const WAY = z.union([
  z.object({ tag: z.literal("way"), at: z.int().check(z.minimum(0)), moves: z.array(MoveJson) }),
  z.object({ tag: z.literal("none") }),
  z.object({ tag: z.literal("unknown") }),
])
const ANSWER = z.union([
  z.object({ id: z.number(), verdict: z.enum(["path", "none", "unknown"]) }),
  z.object({ id: z.number(), way: WAY }),
])

export type Answer =
  | { readonly id: number; readonly verdict: PathVerdict }
  | { readonly id: number; readonly way: Way }

const wayOf = (w: z.output<typeof WAY>): Way => (w.tag === "way" ? { tag: "way", at: w.at, moves: w.moves.map(moveOf) } : w)

export const parseAnswer = (data: unknown): Answer | undefined => {
  const r = ANSWER.safeParse(data)
  if (!r.success) return undefined
  if ("verdict" in r.data) return r.data
  return { id: r.data.id, way: wayOf(r.data.way) }
}

// A way in the file's own spelling of moves, ready to post.
export const wayAnswer = (id: number, way: Way): unknown => ({ id, way: way.tag === "way" ? { ...way, moves: way.moves.map(moveJsonOf) } : way })
